import { useState, useEffect } from 'react';
import { AdminService } from '../services/adminService';

export function useAdmin() {
  const [stats, setStats] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [meetings, setMeetings] = useState<any[]>([]);
  const [meetingTypes, setMeetingTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    
    try {
      // Load all data in parallel
      const [
        statsResult,
        usersResult,
        membersResult,
        meetingsResult,
        meetingTypesResult
      ] = await Promise.all([
        AdminService.getSystemStats(),
        AdminService.getAllUsers(),
        AdminService.getAllMembers(),
        AdminService.getAllMeetings(),
        AdminService.getMeetingTypesWithStats()
      ]);

      if (statsResult.data) setStats(statsResult.data);
      if (usersResult.data) setUsers(usersResult.data);
      if (membersResult.data) setMembers(membersResult.data);
      if (meetingsResult.data) setMeetings(meetingsResult.data);
      if (meetingTypesResult.data) setMeetingTypes(meetingTypesResult.data);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    loadAllData();
  };

  const addConvener = async (userData: any) => {
    const { data, error } = await AdminService.addConvener(userData);
    if (data) {
      await loadAllData();
    }
    return { data, error };
  };

  const updateConvener = async (userId: string, userData: any) => {
    const { data, error } = await AdminService.updateConvener(userId, userData);
    if (data) {
      await loadAllData();
    }
    return { data, error };
  };

  const deleteConvener = async (userId: string) => {
    const { error } = await AdminService.deleteConvener(userId);
    if (!error) {
      await loadAllData();
    }
    return { error };
  };

  const addMember = async (memberData: any) => {
    const { data, error } = await AdminService.addMember(memberData);
    if (data) {
      await loadAllData();
    }
    return { data, error };
  };

  const addMeetingType = async (typeData: { name: string; level: 'State' | 'District' | 'Unit' }) => {
    const { data, error } = await AdminService.addMeetingType(typeData);
    if (data) {
      await loadAllData();
    }
    return { data, error };
  };

  const deleteMeetingType = async (typeId: string) => {
    const result = await AdminService.deleteMeetingType(typeId);
    if (!result.error) {
      await loadAllData();
    }
    return result;
  };

  const updateMeetingStatus = async (meetingId: string, status: string) => {
    const { data, error } = await AdminService.updateMeetingStatus(meetingId, status as any);
    if (data) {
      await loadAllData();
    }
    return { data, error };
  };

  const deleteMeeting = async (meetingId: string) => {
    const { error } = await AdminService.deleteMeeting(meetingId);
    if (!error) {
      await loadAllData();
    }
    return { error };
  };

  return {
    stats,
    users,
    members,
    meetings,
    meetingTypes,
    loading,
    refresh,
    addConvener,
    updateConvener,
    deleteConvener,
    addMember,
    addMeetingType,
    deleteMeetingType,
    updateMeetingStatus,
    deleteMeeting
  };
}
